import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Autocomplete,
  Alert,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuth0 } from "@auth0/auth0-react";
import { selectSelectedSiteObject } from "@/store/selectors";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { fetchLocationsByGdmNumber } from "@/store/slices/locationSlice";
import TurnAroundService, { STATUS_OPTIONS } from "@/services/turnarounds";
import { formatDate, formatToApiDate } from "@/utils/date";
import { formatErrorMessage } from "@/utils/helpers";
import "@/styles/modal.scss";
import { LocationOption } from "@/types/locations";
import { Box } from "@mui/system";
import { Site } from "@/store/slices/siteSlice";
import { DATE_FORMAT_DISPLAY } from "@/constants";
import HierarchicalLocationSelect from "@/components/common/HierarchicalLocationSelect";

interface SiteData {
  site_id: number;
  gdm_customer_number: string;
  site_name: string;
  address_line_1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  default?: boolean;
  world_area?: string;
}

interface SitesResponse {
  sites: SiteData[];
}

interface AddTurnAroundModalProps {
  onClose: () => void;
  onSuccess: () => void;
  existingTurnaround?: any;
}

const AddTurnAroundModal: React.FC<AddTurnAroundModalProps> = ({
  onClose,
  onSuccess,
  existingTurnaround,
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();

  const isEditMode = Boolean(existingTurnaround);

  // Form state
  const [turnaroundName, setTurnaroundName] = useState(existingTurnaround?.name || "");
  const [locationId, setLocationId] = useState<string>("");
    const [status, setStatus] = useState(existingTurnaround?.status || "");
  const [startDate, setStartDate] = useState(existingTurnaround?.start_date || "");
  const [endDate, setEndDate] = useState(existingTurnaround?.end_date || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const locationInitialized = useRef(false);

  // Redux state
  const selectedSiteObject = useAppSelector(selectSelectedSiteObject);
  const { data: userData } = useAppSelector((state) => state.user);
  const locationsData = useAppSelector((state) => state.location.data);
  const locationsLoading = useAppSelector((state) => state.location.loading);

  const turnAroundService = new TurnAroundService();

  // Create display site for the disabled autocomplete
  const displaySite = useMemo(() => {
    if (!selectedSiteObject) return null;
    const { site_id, gdm_customer_number, site_name, address_line_1, city, state, postal_code, country } = selectedSiteObject;
    return {
      id: String(site_id),
      gdm_customer_number,
      name: `${site_name} | ${gdm_customer_number}, ${address_line_1}, ${city}, ${state} ${postal_code} ${country}`,
    };
  }, [selectedSiteObject]);

  // Flatten locations from Redux store
  const flattenedLocations = useMemo(() => {
    if (!locationsData) return [];
    const result: LocationOption[] = [];
    function recurse(items: LocationOption[]) {
      for (const item of items) {
        result.push(item);
        if (Array.isArray(item.children) && item.children.length > 0) {
          recurse(item.children);
        }
      }
    }
    recurse(locationsData);
    return result;
  }, [locationsData]);

  // For edit mode: find locationId based on existingTurnaround (runs only once on initial load)
  useEffect(() => {
    if (
      isEditMode &&
      existingTurnaround?.location &&
      flattenedLocations.length &&
      !locationInitialized.current
    ) {
      const matchedLocation = flattenedLocations.find(
        (l) =>
          l.name &&
          existingTurnaround.location &&
          l.name.toLowerCase() === existingTurnaround.location.toLowerCase()
      );
      if (matchedLocation) {
        setLocationId(String(matchedLocation.id));
        locationInitialized.current = true;
      }
    }
  }, [isEditMode, existingTurnaround, flattenedLocations]);

  // Fetch locations from Redux when site changes
  useEffect(() => {
    const loadLocations = async () => {
      if (!selectedSiteObject?.gdm_customer_number || !userData?.id) return;

      try {
        const accessToken = await getAccessTokenSilently();
        await dispatch(
          fetchLocationsByGdmNumber({
            gdmNumber: selectedSiteObject.gdm_customer_number,
            userGuid: userData.id,
            accessToken,
          })
        ).unwrap();
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };

    loadLocations();
  }, [selectedSiteObject?.gdm_customer_number, userData?.id, dispatch, getAccessTokenSilently]);

  const validateForm = () => {
    if (
      !turnaroundName ||
      !selectedSiteObject?.site_id ||
      !startDate ||
      !endDate ||
      !status
    )
      return false;
    return true;
  };

  // Form change handlers
  const handleTurnaroundNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTurnaroundName(e.target.value);
    setError("");
  };

  const handleSiteChange = (_: any, newValue: any) => {
    setLocationId("");
    setError("");
  };

  const handleLocationChange = (_: any, newValue: any) => {
    setLocationId(newValue?.id ? String(newValue.id) : "");
    setError("");
  };

  const handleStatusChange = (_: any, newValue: any) => {
    setStatus(newValue?.key || "");
    setError("");
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date ? formatDate(date) : "");
    setError("");
  };

  const handleEndDateChange = (date: any) => {
    setEndDate(date ? formatDate(date) : "");
    setError("");
  };

  const handleSubmit = async () => {
    if (!selectedSiteObject?.site_id || !userData?.id) return;
    setLoading(true);
    setError("");

    try {
      const data = {
        name: turnaroundName,
        site_id: Number(selectedSiteObject.site_id),
        location_id: locationId ? Number(locationId) : null,
        status: status || null,
        start_date: formatToApiDate(startDate),
        end_date: formatToApiDate(endDate),
      };

      const response = isEditMode
        ? await turnAroundService.updateTurnaround(existingTurnaround.id, data)
        : await turnAroundService.createTurnaround(data);

      // Check for error in successful response (200 status with error message)
      if (response?.error_message) {
        setError(response.error_message);
        setLoading(false);
        return;
      }

      onSuccess();
      setLoading(false);
    } catch (error) {
      const errorMessage = formatErrorMessage(
        error,
        isEditMode
          ? "Failed to update turnaround"
          : "Failed to create turnaround"
      );
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            className: 'modal-dialog'
          }
        }}
      >
        <Box className="modal-header">
          <DialogTitle className="modal-header-title">
            {isEditMode
              ? t("editTurnaround", "EDIT TURNAROUND")
              : t("createTurnaround", "CREATE TURNAROUND")}
          </DialogTitle>
          <IconButton
            onClick={onClose}
            size="small"
            className="modal-close-button"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent className="modal-content-padded">
          <Typography variant="h6" className="modal-section-title" sx={{ mb: 2 }}>
            {isEditMode
              ? t("editTurnaroundInfo", "Edit Turnaround Information")
              : t("newTurnaroundInfo", "New Turnaround Information")}
          </Typography>
          <form className="form">
            <Grid container spacing={2} sx={{ mb: error ? 2 : 4, mx: "auto" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label className="form-label">{t("turnaroundNameLabel", "TURNAROUND NAME")}</label>
                <TextField
                  label=""
                  variant="outlined"
                  value={turnaroundName}
                  className="input"
                  onChange={handleTurnaroundNameChange}
                  placeholder={t(
                    "enterTurnaroundNamePlaceholder",
                    "Enter Turnaround Name"
                  )}
                  size="medium"
                  fullWidth
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label className="form-label">{t("siteLabel", "SITE")}</label>
                <Autocomplete
                  options={displaySite ? [displaySite] : []}
                  className="input select"
                  getOptionLabel={(option) => option.name || ""}
                  getOptionKey={(option) => option.id || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={displaySite}
                  onChange={handleSiteChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="medium"
                      placeholder={t("moveDeviceModalSelectSite")}
                      fullWidth
                      disabled={loading || isEditMode}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disableClearable={false}
                  disabled={isEditMode || loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label className="form-label">
                  {t("locationLabel", "LOCATION")}
                </label>
                <HierarchicalLocationSelect
                  locationsData={locationsData ?? []}
                  value={locationId}
                  onChange={(newLocationId) => {
                    setLocationId(newLocationId);
                    setError("");
                  }}
                  loading={locationsLoading}
                  disabled={
                    loading || !selectedSiteObject || locationsLoading
                  }
                  placeholder={t("moveDeviceModalSelectLocation")}
                  className="input select"
                  disableClearable={false}
                  fullWidth={true}
                  autoExpandToSelected={true}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label className="form-label">{t("status", "STATUS")}</label>
                <Autocomplete
                  options={STATUS_OPTIONS}
                  className="input select"
                  getOptionLabel={(option) => t(option.labelKey, option.key)}
                  getOptionKey={(option) => option.key}
                  isOptionEqualToValue={(option, value) =>
                    option.key === value.key
                  }
                  value={
                    STATUS_OPTIONS.find((opt) => opt.key === status) || null
                  }
                  onChange={handleStatusChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="medium"
                      placeholder={t(
                        "moveDeviceModalSelectStatus",
                        "Select Status",
                      )}
                      fullWidth
                      disabled={loading}
                    />
                  )}
                  disableClearable={false}
                  disabled={loading}
                />
              </Grid>
              <Grid container size={{ xs: 12, sm: 6 }}>
                <Grid size={{ xs: 6 }}>
                  <label className="form-label">{t("startDateLabel", "START DATE")}</label>
                  <DatePicker
                    value={startDate ? dayjs(startDate) : null}
                    onChange={handleStartDateChange}
                    format={DATE_FORMAT_DISPLAY}
                    enableAccessibleFieldDOMStructure={false}
                    disabled={loading}
                    slots={{
                      textField: (params) => (
                        <TextField
                          {...params}
                          fullWidth
                          className="input"
                          placeholder={t("datePlaceholderYMD", DATE_FORMAT_DISPLAY)}
                          variant="outlined"
                          disabled={loading}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <label className="form-label">{t("endDateLabel", "END DATE")}</label>
                  <DatePicker
                    value={endDate ? dayjs(endDate) : null}
                    onChange={handleEndDateChange}
                    format={DATE_FORMAT_DISPLAY}
                    minDate={startDate ? dayjs(startDate) : undefined}
                    disabled={loading || !startDate}
                    enableAccessibleFieldDOMStructure={false}
                    slots={{
                      textField: (params) => (
                        <TextField
                          {...params}
                          fullWidth
                          className="input"
                          placeholder={t("datePlaceholderYMD", DATE_FORMAT_DISPLAY)}
                          variant="outlined"
                          disabled={loading || !startDate}
                        />
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </form>
          {error && (
            <Alert
              severity="error"
              className="modal-alert-error"
              sx={{ mt: 2 }}
            >
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions className="modal-actions-flex">
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={loading}
            className="modal-button-base modal-button-outlined"
          >
            {t("cancelButton", "CANCEL")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            className="modal-button-base modal-button-primary"
            disabled={
              !validateForm() || loading || locationsLoading
            }
            data-testid="submit-turnaround-button"
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              t("updateTurnaround", "UPDATE TURNAROUND")
            ) : (
              t("createTurnaround", "CREATE TURNAROUND")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddTurnAroundModal;
